import React, { useState, useEffect } from 'react';
import { Card, Tag, Typography, Image, Grid, theme, Spin, message, Layout } from 'antd';
import { useParams } from 'react-router-dom';
import { HttpRequest } from '../../../../../../utils/httpClient';
import { useAssistant } from '../../../../../../contexts/AssistantContext';
import { FormAuthIntegration } from '../FormAuthIntegration/formAuthIntegration';
import { IntegrationFunctions } from '../IntegrationFunctions/IntegrationFunctions';
import { FormIntegrationFunction } from '../FormIntegrationFunction/formIntegrationFunction';
import { ConfirmConnection } from '../ConfirmConnection/ConfirmConnection';
import { CustomIntegrationSelector } from '../CustomIntegrationSelector/CustomIntegrationSelector';
import { QueueSelector } from '../QueueSelector/QueueSelector';
import { ProcessFlowSelector } from '../ProcessFlowSelector/ProcessFlowSelector';
import { Queue } from '../../../../../../types/queue';
import n8nLogo from '../../../../../../assets/img/n8n-logo.svg';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { useToken } = theme;
const { useBreakpoint } = Grid;
const { Title, Paragraph } = Typography;

interface IntegrationCardProps {
  logo: string;
  name: string;
  description: string;
  authenticated: boolean;
  authFields: Array<{
    name: string;
    label: string;
    placeholder: string;
    type: string;
    required: boolean;
    order: number;
  }>;
  id: string;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  logo,
  name,
  description,
  authenticated,
}) => {
  const { token } = useToken();

  return (
    <Card
      hoverable
      style={{ height: '100%' }}
      cover={
        <Image
          alt={`${name} logo`}
          src={logo}
          preview={false}
          style={{ padding: token.padding, objectFit: 'contain', height: 80 }}
        />
      }
    >
      <Tag color={authenticated ? 'success' : 'warning'} style={{ marginBottom: token.marginXS }}>
        {authenticated ? 'Authenticated' : 'Not authenticated'}
      </Tag>
      <Title level={5} style={{ margin: `${token.marginXS}px 0` }}>
        {name}
      </Title>
      <Paragraph type="secondary" style={{ margin: 0 }}>
        {description}
      </Paragraph>
    </Card>
  );
};

interface IntegrationSelectorProps {
  onSave: (message: string) => void;
  chatbotId?: string;
  flowId?: string;
}

export const IntegrationSelector: React.FC<IntegrationSelectorProps> = ({
  onSave,
  chatbotId: propChatbotId,
  flowId,
}) => {
  const { token } = useToken();
  const screens = useBreakpoint();
  const { currentAssistant } = useAssistant();
  const { accountId } = useParams();
  const chatbotId = propChatbotId || currentAssistant?.id;
  const [apps, setApps] = useState<IntegrationCardProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationCardProps | null>(null);
  const [view, setView] = useState<'list' | 'functions' | 'function-form' | 'auth'>('list');
  const [selectedFunction, setSelectedFunction] = useState<any>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [showFunctionForm, setShowFunctionForm] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [showQueueSelector, setShowQueueSelector] = useState(false);
  const [showProcessFlowSelector, setShowProcessFlowSelector] = useState(false);

  // Remover el uso de localStorage para persistir estados
  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        const request = new HttpRequest(`${process.env.REACT_APP_API_HOST}/integration`);
        const response = await request.get('');

        // Fetch authentication status
        const authRequest = new HttpRequest(
          `${process.env.REACT_APP_API_HOST}/integration/auth/${chatbotId}`,
        );
        const authResponse = await authRequest.get('');
        const authenticatedIntegrations = authResponse.data.auths || [];

        logger.info('chatbotId:', chatbotId);
        logger.info('response:', authResponse);
        logger.info('authenticatedIntegrations:', authenticatedIntegrations);

        const transformedApps = response.data.integrations.map((integration: any) => ({
          logo: integration.logo || 'https://placehold.co/200x100?text=Default',
          name: integration.name,
          description: integration.description,
          authenticated: authenticatedIntegrations.some(
            (auth: any) => auth.integrationId === integration.id,
          ),
          authFields: integration.authFields || [],
          id: integration.id,
        }));

        // Agregar integraciones especiales al inicio de la lista
        const customIntegration: IntegrationCardProps = {
          logo: 'https://placehold.co/200x100/007bff/ffffff?text=Custom',
          name: 'Custom Integration',
          description: 'Create your own custom integration with custom prompts and document uploads',
          authenticated: true,
          authFields: [],
          id: 'custom-integration',
        };

        const processFlowIntegration: IntegrationCardProps = {
          logo: n8nLogo,
          name: 'Process Flow',
          description: 'Execute automated workflows with configurable parameters',
          authenticated: true,
          authFields: [],
          id: 'process-flow-integration',
        };

        setApps([customIntegration, processFlowIntegration, ...transformedApps]);
      } catch (error) {
        logger.error('Error fetching integrations:', error);
        message.error('Failed to load integrations');
      } finally {
        setLoading(false);
      }
    };

    if (chatbotId) {
      fetchIntegrations();
    }
  }, [chatbotId]);

  // Limpiar todos los estados cuando se cierra el selector
  useEffect(() => {
    return () => {
      setSelectedIntegration(null);
      setView('list');
      setSelectedFunction(null);
      setShowConfirmation(false);
      setShowAuthForm(false);
      setShowFunctionForm(false);
      setShowCustomForm(false);
      setShowQueueSelector(false);
      setShowProcessFlowSelector(false);
    };
  }, []);

  const handleCardClick = (app: IntegrationCardProps) => {
    // Manejar la integración personalizada de manera especial
    if (app.id === 'custom-integration') {
      setSelectedIntegration(app);
      setShowCustomForm(true);
      return;
    }

    // Manejar Process Flow de manera especial
    if (app.id === 'process-flow-integration') {
      setSelectedIntegration(app);
      setShowProcessFlowSelector(true);
      return;
    }

    // Manejar Assign to Queue de manera especial
    if (app.name === 'Assign to Queue') {
      setSelectedIntegration(app);
      setShowQueueSelector(true);
      return;
    }

    if (!app.authenticated) {
      setSelectedIntegration(app);
      setShowConfirmation(true);
    } else {
      setSelectedIntegration(app);
      setView(app.authenticated ? 'functions' : 'auth');
    }
  };

  const handleBack = () => {
    setView('list');
    setSelectedIntegration(null);
  };

  const handleAuthSuccess = () => {
    if (selectedIntegration) {
      // Actualizar la integración seleccionada
      const updatedIntegration = {
        ...selectedIntegration,
        authenticated: true,
      };

      // Actualizar la lista de apps
      setApps((prevApps) =>
        prevApps.map((app) => (app.name === selectedIntegration.name ? updatedIntegration : app)),
      );

      // Actualizar la integración seleccionada
      setSelectedIntegration(updatedIntegration);

      // Cambiar directamente a la vista de funciones
      setShowAuthForm(false);
      setView('functions');
    }
  };

  const handleFunctionSelect = (functionData: any) => {
    setSelectedFunction(functionData);
    setShowFunctionForm(true);
    setView('function-form');
  };

  const handleQueueSelect = (queue: Queue) => {
    // Crear el mensaje de configuración para Assign to Queue
    const queueConfig = {
      type: 'queue',
      queueId: queue.id,
      queueName: queue.name,
      chatbotId: chatbotId,
      action: 'assign',
      description: `Assign conversation to ${queue.name} queue`,
      timestamp: new Date().toISOString()
    };

    // Crear el mensaje con el formato correcto para que AssistantFlowNew lo detecte
    const messageString = `@queue/assign ${JSON.stringify(queueConfig)}`;
    
    // Guardar la configuración
    onSave(messageString);
    
    // Limpiar estados
    setShowQueueSelector(false);
    setSelectedIntegration(null);
  };

  const handleQueueCancel = () => {
    setShowQueueSelector(false);
    setSelectedIntegration(null);
  };

  const handleProcessFlowSave = (messageString: string) => {
    onSave(messageString);

    // Limpiar estados
    setShowProcessFlowSelector(false);
    setSelectedIntegration(null);
  };

  const handleProcessFlowCancel = () => {
    setShowProcessFlowSelector(false);
    setSelectedIntegration(null);
  };

  if (loading) {
    return (
      <Layout>
        <div
          style={{
            padding: token.padding,
            height: '70vh', // Igual que el contenedor principal
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#fff',
            minWidth: 0, // Para evitar overflow horizontal
          }}
        >
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  if (showConfirmation && selectedIntegration) {
    return (
      <ConfirmConnection
        integration={selectedIntegration}
        onCancel={() => {
          setShowConfirmation(false);
          setSelectedIntegration(null);
        }}
        onConfirm={() => {
          setShowConfirmation(false);
          setShowAuthForm(true);
        }}
      />
    );
  }

  if (showAuthForm && selectedIntegration && chatbotId) {
    return (
      <FormAuthIntegration
        integration={selectedIntegration}
        chatbotId={chatbotId}
        onBack={() => {
          setShowAuthForm(false);
          setSelectedIntegration(null);
          setView('list'); // Asegurarse de volver a la lista si se cancela
        }}
        onAuthSuccess={handleAuthSuccess}
      />
    );
  }

  if (view === 'functions' && selectedIntegration) {
    return (
      <IntegrationFunctions
        integration={selectedIntegration}
        onBack={() => {
          setView('list');
          setSelectedIntegration(null);
        }}
        onFunctionSelect={handleFunctionSelect}
      />
    );
  }

  if (showFunctionForm && selectedFunction) {
    return (
      <FormIntegrationFunction
        functionData={selectedFunction}
        onBack={() => {
          setShowFunctionForm(false);
          setView('functions');
        }}
        onSave={onSave}
        chatbotId={chatbotId}
        flowId={flowId}
      />
    );
  }

  if (showCustomForm && selectedIntegration) {
    return (
      <CustomIntegrationSelector
        onBack={() => {
          setShowCustomForm(false);
          setSelectedIntegration(null);
          setView('list');
        }}
        onSave={onSave}
        chatbotId={chatbotId}
        flowId={flowId}
      />
    );
  }

  if (showQueueSelector && chatbotId && accountId) {
    return (
      <QueueSelector
        chatbotId={chatbotId}
        accountId={accountId}
        onCancel={handleQueueCancel}
        onConfirm={handleQueueSelect}
      />
    );
  }

  if (showProcessFlowSelector && chatbotId) {
    return (
      <ProcessFlowSelector
        assistantId={chatbotId}
        onSave={handleProcessFlowSave}
        onCancel={handleProcessFlowCancel}
      />
    );
  }

  // Calcula el número de columnas basado en el breakpoint, máximo 3
  const getGridColumns = () => {
    if (screens.md) return 3; // Medium and larger screens
    if (screens.sm) return 2; // Small screens
    return 1; // Extra small screens
  };

  return (
    <Layout>
      {!showConfirmation && !showAuthForm && !showFunctionForm && !showCustomForm && !showQueueSelector && !showProcessFlowSelector && (
        <div
          style={{
            padding: token.padding,
            height: '70vh', // Asegura que tome toda la altura disponible
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // Previene scroll en el contenedor principal
            background: '#fff',
          }}
        >
          <Title level={5} style={{ marginBottom: token.marginLG }}>
            Select the app you want to integrate to your flow
          </Title>
          <div
            style={{
              flex: 1,
              height: 'auto',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${getGridColumns()}, 1fr)`,
                gap: token.marginMD,
                padding: `${token.paddingSM}px 0`,
                maxWidth: '1200px', // Limita el ancho máximo del grid
                margin: '0 auto', // Centra el grid
              }}
            >
              {apps.map((app, index) => (
                <div key={index} onClick={() => handleCardClick(app)} style={{ cursor: 'pointer' }}>
                  <IntegrationCard {...app} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
